import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Search, Settings, Download, Layers, X, Shield, Trash2, Check, ChevronDown, ChevronUp } from "lucide-react";

const RadarLogo = ({ className = "h-10 w-auto" }) => (
  <img
    src="/tabfinder-logo.png"
    alt="Tab Finder"
    className={className}
  />
);

export default function DemoPage() {
  const [activeTab, setActiveTab] = useState("duplicates");
  const [searchQuery, setSearchQuery] = useState("");
  const [expanded, setExpanded] = useState("g1");
  const [toast, setToast] = useState(null);
  const [groupCount, setGroupCount] = useState(3);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary/50 to-background py-6">
      <main className="mx-auto max-w-[920px] px-4">
        <div className="mx-auto max-w-lg">
          <Card className="shadow-2xl border-2 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-primary-dark p-4">
              <div className="flex items-center justify-between mb-4">
                <RadarLogo className="h-8 w-auto" />
                <div className="flex gap-2">
                  <button className="p-1.5 rounded-md bg-primary-foreground/10 hover:bg-primary-foreground/20">
                    <Download className="w-4 h-4 text-primary-foreground" />
                  </button>
                  <button className="p-1.5 rounded-md bg-primary-foreground/10 hover:bg-primary-foreground/20">
                    <Settings className="w-4 h-4 text-primary-foreground" />
                  </button>
                </div>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3 p-3 bg-primary-foreground/10 rounded-lg">
                <div className="text-center">
                  <div className="text-xl font-bold text-primary-foreground">3</div>
                  <div className="text-[10px] text-primary-foreground/70 uppercase">Windows</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-primary-foreground">47</div>
                  <div className="text-[10px] text-primary-foreground/70 uppercase">Tabs</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-accent">{groupCount}</div>
                  <div className="text-[10px] text-primary-foreground/70 uppercase">Groups</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-accent">{groupCount * 2}</div>
                  <div className="text-[10px] text-primary-foreground/70 uppercase">Dupes</div>
                </div>
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full grid grid-cols-2 rounded-none border-b h-auto p-0">
                <TabsTrigger value="duplicates" className="flex items-center gap-2 py-3 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
                  <Copy className="w-4 h-4" />Duplicates
                </TabsTrigger>
                <TabsTrigger value="search" className="flex items-center gap-2 py-3 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
                  <Search className="w-4 h-4" />Search
                </TabsTrigger>
              </TabsList>

              {/* Duplicates Tab */}
              <TabsContent value="duplicates" className="m-0">
                {groupCount > 0 ? (
                  <div>
                    <div className="p-3 border-b bg-muted/50">
                      <Button onClick={() => { setGroupCount(0); showToast('All duplicates merged!'); }} className="w-full bg-accent hover:bg-accent-dark text-accent-foreground">
                        <Layers className="w-4 h-4 mr-2" />Merge All Duplicates
                      </Button>
                    </div>
                    <div className="p-3 space-y-3 max-h-[400px] overflow-y-auto">
                      {/* Group 1 */}
                      <div className="border border-border rounded-xl overflow-hidden bg-card">
                        <button onClick={() => setExpanded(expanded === "g1" ? "" : "g1")} className="w-full flex items-center gap-3 p-3 bg-muted/50 hover:bg-muted transition-colors text-left">
                          <div className="w-5 h-5 rounded bg-gray-800 flex items-center justify-center text-xs font-bold text-primary-foreground">G</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">GitHub</div>
                            <div className="text-xs text-muted-foreground truncate">https://github.com</div>
                          </div>
                          <Badge className="bg-accent/20 text-accent border-0">3</Badge>
                          <button onClick={(e) => { e.stopPropagation(); setGroupCount(p => p - 1); showToast('Domain whitelisted'); }} className="p-1.5 rounded-md hover:bg-background text-muted-foreground"><Shield className="w-4 h-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); setGroupCount(p => p - 1); showToast('Duplicates closed'); }} className="p-1.5 rounded-md hover:bg-destructive hover:text-destructive-foreground text-muted-foreground"><Trash2 className="w-4 h-4" /></button>
                          {expanded === "g1" ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                        </button>
                        {expanded === "g1" && (
                          <div className="divide-y divide-border">
                            <div className="flex items-center gap-3 p-2.5 px-3 bg-primary/10">
                              <div className="w-4 h-4 rounded-full border-2 border-primary flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-primary" /></div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-foreground truncate">GitHub - Repository</div>
                                <div className="text-[10px] text-muted-foreground">Window 1 • Tab 3</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 p-2.5 px-3 hover:bg-muted/50">
                              <div className="w-4 h-4 rounded-full border-2 border-border flex items-center justify-center"></div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-foreground truncate">GitHub - Issues</div>
                                <div className="text-[10px] text-muted-foreground">Window 2 • Tab 7</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 p-2.5 px-3 hover:bg-muted/50">
                              <div className="w-4 h-4 rounded-full border-2 border-border flex items-center justify-center"></div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-foreground truncate">GitHub - Pull Requests</div>
                                <div className="text-[10px] text-muted-foreground">Window 3 • Tab 1</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Group 2 */}
                      <div className="border border-border rounded-xl overflow-hidden bg-card">
                        <button onClick={() => setExpanded(expanded === "g2" ? "" : "g2")} className="w-full flex items-center gap-3 p-3 bg-muted/50 hover:bg-muted transition-colors text-left">
                          <div className="w-5 h-5 rounded bg-orange-500 flex items-center justify-center text-xs font-bold text-primary-foreground">S</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">Stack Overflow</div>
                            <div className="text-xs text-muted-foreground truncate">https://stackoverflow.com</div>
                          </div>
                          <Badge className="bg-accent/20 text-accent border-0">2</Badge>
                          <button onClick={(e) => { e.stopPropagation(); setGroupCount(p => p - 1); showToast('Duplicates closed'); }} className="p-1.5 rounded-md hover:bg-destructive hover:text-destructive-foreground text-muted-foreground"><Trash2 className="w-4 h-4" /></button>
                          {expanded === "g2" ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                        </button>
                        {expanded === "g2" && (
                          <div className="divide-y divide-border">
                            <div className="flex items-center gap-3 p-2.5 px-3 bg-primary/10">
                              <div className="w-4 h-4 rounded-full border-2 border-primary flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-primary" /></div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-foreground truncate">Stack Overflow - Question</div>
                                <div className="text-[10px] text-muted-foreground">Window 1 • Tab 5</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 p-2.5 px-3 hover:bg-muted/50">
                              <div className="w-4 h-4 rounded-full border-2 border-border flex items-center justify-center"></div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-foreground truncate">Stack Overflow - Answer</div>
                                <div className="text-[10px] text-muted-foreground">Window 2 • Tab 12</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Group 3 */}
                      <div className="border border-border rounded-xl overflow-hidden bg-card">
                        <button onClick={() => setExpanded(expanded === "g3" ? "" : "g3")} className="w-full flex items-center gap-3 p-3 bg-muted/50 hover:bg-muted transition-colors text-left">
                          <div className="w-5 h-5 rounded bg-red-500 flex items-center justify-center text-xs font-bold text-primary-foreground">Y</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">YouTube</div>
                            <div className="text-xs text-muted-foreground truncate">https://youtube.com</div>
                          </div>
                          <Badge className="bg-accent/20 text-accent border-0">4</Badge>
                          <button onClick={(e) => { e.stopPropagation(); setGroupCount(p => p - 1); showToast('Duplicates closed'); }} className="p-1.5 rounded-md hover:bg-destructive hover:text-destructive-foreground text-muted-foreground"><Trash2 className="w-4 h-4" /></button>
                          {expanded === "g3" ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
                      <Check className="w-8 h-8 text-success" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">No Duplicates Found</h3>
                    <p className="text-sm text-muted-foreground">Your tabs are unique!</p>
                  </div>
                )}
              </TabsContent>

              {/* Search Tab */}
              <TabsContent value="search" className="m-0">
                <div className="p-3 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search tabs..." className="pl-10" />
                  </div>
                </div>
                <div className="p-3 max-h-[400px] overflow-y-auto">
                  {searchQuery ? (
                    <div className="space-y-2">
                      <div onClick={() => showToast('Focused: GitHub - Repository')} className="flex items-center gap-3 p-3 rounded-xl cursor-pointer bg-primary/10 border border-primary/30">
                        <div className="w-5 h-5 rounded bg-gray-800 flex items-center justify-center text-xs font-bold text-primary-foreground">G</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-foreground truncate">GitHub - Repository</div>
                          <div className="text-xs text-muted-foreground">Window 1 • Tab 3</div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); showToast('Tab closed'); }} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
                      </div>
                      <div onClick={() => showToast('Focused: Google Search')} className="flex items-center gap-3 p-3 rounded-xl cursor-pointer hover:bg-muted border border-transparent">
                        <div className="w-5 h-5 rounded bg-blue-500 flex items-center justify-center text-xs font-bold text-primary-foreground">G</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-foreground truncate">Google Search Results</div>
                          <div className="text-xs text-muted-foreground">Window 1 • Tab 1</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Search className="w-12 h-12 text-primary/50 mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">Search Your Tabs</h3>
                      <p className="text-sm text-muted-foreground">Type to search across all windows.</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg bg-success text-success-foreground flex items-center gap-3 animate-fade-up">
          <Check className="w-4 h-4" />
          <span className="text-sm font-medium">{toast}</span>
        </div>
      )}
    </div>
  );
}
